<script setup lang="ts">
import { computed } from 'vue';
import FilterGroup from '@/core/components/form-elements/FilterGroup.vue';
import { AvailableFilter, FilterOption } from '@/modules/content-blocks/interfaces/jobs-block.interface';

interface AggregationItem {
   value: number;
   name?: string;
   count: number;
}

const props = defineProps<{
   availableFilters: { filter_elements: AvailableFilter[] };
   aggregations: Record<string, AggregationItem[]>;
   showJobTypeFilter: boolean;
   showScheduleFilter: boolean;
   showCityFilter: boolean;
}>();

const jobTypeSelection = defineModel<{ value: number }[]>('jobTypes', { default: [] });
const workScheduleSelection = defineModel<number[]>('workSchedules', { default: [] });
const citySelection = defineModel<number[]>('cities', { default: [] });

const availableFilterElements = computed(() => props.availableFilters?.filter_elements ?? []);

// create filter options from aggregations,
// with labels resolved from available filter elements OR the aggregation name (for cities, not in availableOptions (/filters endpoint))
function buildFilterOptions(filterKey: string): FilterOption[] {
   const availableOptions = availableFilterElements.value.find((filter) => filter.key === filterKey)?.options || [];
   const aggregationsFromApi = props.aggregations?.[filterKey] || [];
   const optionsMap = new Map(availableOptions.map((opt) => [String(opt.value), opt.label]));

   return aggregationsFromApi.map((option) => ({
      value: option.value,
      label: optionsMap.get(String(option.value)) ?? option.name ?? '',
      count: option.count,
   }));
}

const jobFunctions = computed(() => buildFilterOptions('job_types'));
const jobSchedules = computed(() => buildFilterOptions('work_schedules'));
const cities = computed(() => buildFilterOptions('cities'));
</script>

<template>
   <aside class="jobs-filters">
      <FilterGroup
         v-if="showCityFilter"
         v-model="citySelection"
         :title="$tb('live-editor.content-blocks.job_board.filters.city')"
         :options="cities"
         use-city-format
      />

      <FilterGroup
         v-if="showJobTypeFilter"
         v-model="jobTypeSelection"
         :title="$tb('live-editor.content-blocks.job_board.filters.job_type')"
         :options="jobFunctions"
         use-object-format
      />

      <FilterGroup
         v-if="showScheduleFilter"
         v-model="workScheduleSelection"
         :title="$tb('live-editor.content-blocks.job_board.filters.schedule')"
         :options="jobSchedules"
      />
   </aside>
</template>

<style scoped lang="scss">
.jobs-filters {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
   padding: var(--nxt-gutter);

   :deep(.nxt-checkbox) input {
      &:checked + label::after {
         background-color: var(--nxt-dark);
      }
   }
}
</style>
